import React, { useEffect, useState, useContext, useRef } from 'react'
import { ChatRoom } from './ChatRoom'
import {
  setAliOpenSideBar,
  setFullScreen,
  setSelectedAgent,
  setSelectedChatModel
} from '../../../_redux/actions/chatbotActions'
import { checkIsDrawerMode, buildDataboxODataFilter } from './utils/helpers'
import { MCP_SERVER_NAMES } from './utils/constants'
import { useDispatch, useSelector } from 'react-redux'
import ChatHeader from './ChatHeader'
import { useChatService } from './services/chatService'
import { ChatHistory } from './ChatHistory'
import { ChatbotContext } from './context/ChatbotProvider'
import { Flyer } from '../../../@core/containers/Ali/Flyer'
import { IntroGuideTour } from '../../_organisms/_index'
import { chatSteps } from '../../_molecules/chatSteps'
import { AliVideo } from '../../../configs/constants'
import Citations from './side-panel/Citations'
//import ImagePreviewModal from './modals/ImagePreviewModal'
import ComparisonWindow from './side-panel/ComparisonWindow'
import { CodeWindow } from './side-panel/CodeWindow'
import { useLocation, useNavigate } from 'react-router-dom'
import ManageAgents from './agent/ManageAgents'
import { useAgentService } from './agent/agentService'
import { useFeatureGate } from '../../../_hooks/useFeatureGate'
import ImagePreviewModal from './modals/ImagePreviewModal'
import ProjectView from './ProjectView'
import { resetPendingDataSourcesCache } from './DataSourceSelect'

const ChatWindow: React.FC = () => {
  const {
    messages,
    setRawMessages,
    setDisableInput,
    setCurrentChat,
    selectedMessage,
    setSelectedMessage,
    attachments,
    setAttachments,
    setDataProductsInChat,
    setThreadMcpServers,
    setThreadPersonalDataBoxes,
    setThreadDataboxIds,
    setThreadDataProductIds,
    setThreadCognitiveSearchIds,
    setThreadDataProductsByDatabox,
    setThreadType,
    selectedProject,
    setIsWebSearchEnabled
  } = useContext(ChatbotContext)

  const dispatch = useDispatch()
  const [showSetting, setShowSetting] = useState(false)
  const { cancelAPICall } = useChatService()
  const {
    getAgentsListByUser,
    getModels,
    getAllAgents,
    isGettingAllAgents,
    isGettingMyAgents,
    getAllMCPServers,
    getAgentDetails
  } = useAgentService()

  const [showHistory, setShowHistory] = useState(false)
  const {
    userMetaData: { isRegisteredForAli }
  } = useSelector((state: any) => state?.profileReducer?.profile)
  const [hasAttested, setHasAttested] = useState(isRegisteredForAli)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const profileInfo = useSelector((state: any) => state?.profileReducer?.profile)
  const isAliTourCompleted = profileInfo?.userMetaData?.isAliTourCompleted
  const [isAliTourTriggered, setIsAliTourTriggered] = useState(false)
  const isDrawerMode = checkIsDrawerMode()
  const isRightPanelOpen =
    selectedMessage &&
    (selectedMessage?.type === 'compare' ||
      selectedMessage?.type === 'maximize' ||
      selectedMessage?.type === 'citations')
  const navigate = useNavigate()
  const { openSidebar, selectedAgent, chatModels, mcpServers } = useSelector(
    (state: any) => state.chatbotReducer
  )
  const assistants = useSelector((state: any) => state.agentReducer.agentsList)
  const isLoadingAgents = isGettingAllAgents || isGettingMyAgents
  const location = useLocation()
  const isDataMarketplace =
    location.pathname.startsWith('/data-marketplace/') ||
    location.pathname.startsWith('/data-product-admin/')

  const isChat =
    !location.pathname.includes('agent') ||
    location.pathname.includes('/workspace') ||
    location.pathname.includes('/data-marketplace') ||
    location.pathname.includes('/data-product-admin')

  const { value: centrali3_0, isLoading: flagLoading } = useFeatureGate('centrali3_0', false)

  useEffect(() => {
    if (!flagLoading) {
      if (!isDataMarketplace && centrali3_0) {
        getAgentsListByUser()
      } else {
        getAllAgents(isDataMarketplace)
      }
      getModels()
      getAllMCPServers()
      //not attested and full screen ali => open attestation modal
      if (!hasAttested && !isDrawerMode) setIsModalOpen(true)
      if (!isDrawerMode) setShowHistory(true)
    }
  }, [centrali3_0, flagLoading])

  // Map selectedAgent's modelId to the matching model from chatModels
  useEffect(() => {
    if (selectedAgent?.modelId && chatModels && chatModels.length > 0) {
      const matchingModel = chatModels.find((model: any) => model.id === selectedAgent.modelId)
      if (matchingModel) {
        dispatch(setSelectedChatModel(matchingModel))
      }
    }
  }, [selectedAgent, chatModels])

  // Restore missing agent MCP settings on selection without duplicate requests or stale updates.
  const agentConfigCacheRef = useRef<
    Map<string, { agentMcpServers: any[]; personalDataBoxes: any }>
  >(new Map())
  const pendingAgentDetailsRef = useRef<Set<string>>(new Set())
  const latestSelectedAgentRef = useRef<any>(selectedAgent)
  useEffect(() => {
    // Keep async responses aligned with the latest selected agent.
    latestSelectedAgentRef.current = selectedAgent
  }, [selectedAgent])

  useEffect(() => {
    const agentId = selectedAgent?.id
    if (!agentId || selectedAgent?.isDeleted || typeof getAgentDetails !== 'function') return

    const mergeAgentConfig = (config: { agentMcpServers: any[]; personalDataBoxes: any }) => {
      const latestAgent = latestSelectedAgentRef.current
      // Ignore responses for another agent or an agent that already has its configuration.
      if (latestAgent?.id !== agentId || !config.agentMcpServers.length) return
      if (Array.isArray(latestAgent?.agentMcpServers) && latestAgent.agentMcpServers.length > 0) {
        return
      }
      dispatch(
        setSelectedAgent({
          ...latestAgent,
          agentMcpServers: config.agentMcpServers,
          personalDataBoxes: latestAgent?.personalDataBoxes ?? config.personalDataBoxes
        })
      )
    }

    if (Array.isArray(selectedAgent?.agentMcpServers) && selectedAgent.agentMcpServers.length > 0) {
      // Refresh the cache when a complete agent configuration is selected.
      agentConfigCacheRef.current.set(agentId, {
        agentMcpServers: selectedAgent.agentMcpServers,
        personalDataBoxes: selectedAgent.personalDataBoxes
      })
      return
    }

    const cachedConfig = agentConfigCacheRef.current.get(agentId)
    if (cachedConfig) {
      // Restore settings if a later listing selection omits the MCP configuration.
      mergeAgentConfig(cachedConfig)
      return
    }
    // Avoid fetching the same agent while its details request is already pending.
    if (pendingAgentDetailsRef.current.has(agentId)) return
    pendingAgentDetailsRef.current.add(agentId)

    // Fetch missing settings once, cache them, and merge only into the current agent.
    Promise.resolve(getAgentDetails(agentId))
      .then((res: any) => {
        const fullAgent = res?.agent
        const config = {
          agentMcpServers: Array.isArray(fullAgent?.agentMcpServers)
            ? fullAgent.agentMcpServers
            : [],
          personalDataBoxes: fullAgent?.personalDataBoxes
        }
        agentConfigCacheRef.current.set(agentId, config)
        mergeAgentConfig(config)
      })
      .catch((error: any) => {
        console.error('Failed to load agent details for chat:', error)
      })
      .finally(() => {
        // Allow a later selection to retry if this request failed.
        pendingAgentDetailsRef.current.delete(agentId)
      })
  }, [selectedAgent?.id, selectedAgent?.agentMcpServers])

  const closeAliDrawer = () => {
    dispatch(setAliOpenSideBar(false))
  }

  const onNewConversationStart = async (navigateToChat: boolean = true) => {
    if (selectedAgent && selectedAgent?.isDeleted) {
      const personalAssistant = assistants?.find((agent: any) => agent.isPersonalAssistant === true)

      if (personalAssistant) {
        dispatch(setSelectedAgent(personalAssistant))
      }
    }
    void cancelAPICall(false)
    setCurrentChat({ id: null, title: null })
    setDisableInput(false)
    setRawMessages([])
    setAttachments({ ...attachments, count: 0, previewFile: null })
    setSelectedMessage(null)
    setDataProductsInChat([])
    setThreadMcpServers([])
    setThreadPersonalDataBoxes([])
    setThreadDataboxIds([])
    setThreadDataProductIds([])
    setThreadCognitiveSearchIds([])
    setThreadDataProductsByDatabox([])
    setThreadType(null)
    setIsWebSearchEnabled(null)
    if (!isDrawerMode && navigateToChat) {
      navigate(`/chat`)
    }
  }

  // Handle navigation state changes (agent card navigation, document selection)
  useEffect(() => {
    // Handle navigation from agent card
    if (location.pathname === '/chat' && location.state?.fromAgentCard) {
      onNewConversationStart(false)
    }

    // Handle document selection from DataboxDetailPage navigation
    const state = location.state as {
      fromDatabox?: boolean
      scope?: 'Personal' | 'Team'
      databoxId?: string
      smartDocsDataboxId?: string
      smartDocsTeamId?: string
      selectedFileItems?: Array<{
        type: string
        smartDocsDocumentId?: string | number
        cognitiveSearchId?: string
      }>
      // Data products selected alongside files - "Chat" allows chatting with everything selected.
      selectedDataProductItems?: Array<{ type: string; id: string | number }>
    }

    if (state?.fromDatabox) {
      // Otherwise a stale cached selection can win over this fresh one (chatId stays null -> null).
      resetPendingDataSourcesCache()

      // Personal Databox scope goes straight into the thread's own personalDataBoxes field -
      if (state.scope === 'Personal') {
        const resourceIds = (state.selectedFileItems || [])
          .map((item) => String(item.smartDocsDocumentId ?? ''))
          .filter((id) => Boolean(id))

        if (state.databoxId && resourceIds.length > 0) {
          setThreadPersonalDataBoxes([
            {
              personalDataBoxId: state.databoxId,
              resourceIds
            }
          ])
        }

        // Clear location state to prevent re-initialization on subsequent renders
        window.history.replaceState({}, document.title)
        return
      }

      // `mcpServers` loads asynchronously - wait for it instead of clearing location.state below, otherwise this block would be skipped for good once it finally arrives
      if (!mcpServers || mcpServers.length === 0) {
        return
      }

      // build a real SmartInsights entry here
      const smartInsightsServer = mcpServers?.find(
        (server: any) => server?.name === MCP_SERVER_NAMES.SMART_INSIGHTS
      )
      const filters = state.databoxId
        ? buildDataboxODataFilter([
            {
              id: state.databoxId,
              smartDocsDataboxId: state.smartDocsDataboxId,
              selectedItems: state.selectedFileItems
            }
          ])
        : ''

      const newThreadMcpServers: any[] = []

      if (smartInsightsServer && filters) {
        newThreadMcpServers.push({
          mcpServerId: smartInsightsServer.id,
          isEnabled: true,
          scopeConfiguration: {
            teamIdentifier: state.smartDocsTeamId,
            filters
          }
        })
      }

      // Data products from a databox go out via their own Snowflake MCP entry, nested under the databoxId.
      const dataProductIds = (state.selectedDataProductItems || [])
        .map((item) => String(item.id))
        .filter(Boolean)
      const snowflakeServer = mcpServers?.find(
        (server: any) => server?.name === MCP_SERVER_NAMES.SNOWFLAKE
      )

      if (snowflakeServer && dataProductIds.length > 0) {
        // Must match the id buildDataboxODataFilter embeds, or the two MCP entries render as separate cards.
        const groupDataboxId = state.smartDocsDataboxId || state.databoxId
        newThreadMcpServers.push({
          mcpServerId: snowflakeServer.id,
          isEnabled: true,
          scopeConfiguration: {
            dataProductIds,
            customSelection: {
              dataProducts: [{ databoxId: groupDataboxId, dataProductIds }]
            }
          }
        })
      }

      if (newThreadMcpServers.length > 0) {
        setThreadMcpServers(newThreadMcpServers)
      }

      // Clear location state to prevent re-initialization on subsequent renders
      window.history.replaceState({}, document.title)
    }
  }, [location.key, mcpServers])

  const closeImagePreview = () => {
    setAttachments({ ...attachments, previewFile: null })
  }

  return (
    <div
      className={`${isDrawerMode ? 'block' : 'flex'} overflow-hidden h-full theme-surface relative`}
      data-testid="chat-window">
      {((hasAttested && !isAliTourCompleted && !isDrawerMode) || isAliTourTriggered) && (
        <IntroGuideTour
          introSteps={chatSteps(messages.length > 0)}
          section="chat"
          isAliTourTriggered={isAliTourTriggered}
          setIsAliTourTriggered={setIsAliTourTriggered}
        />
      )}
      <ChatHistory
        data-testid="chat-history"
        showHistory={showHistory}
        setShowHistory={setShowHistory}
        onNewConversationStart={onNewConversationStart}
        isAliOpen={openSidebar}
      />
      <div className="flex flex-col flex-1 theme-surface z-10 w-full mr-2 overflow-hidden h-full transition-[flex,transform] duration-700 ease-in-out">
        <ChatHeader
          data-testid="chat-header"
          showSetting={showSetting}
          setShowSetting={setShowSetting}
          onNewConversationStart={onNewConversationStart}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          setIsAliTourTriggered={setIsAliTourTriggered}
          isRightPanelOpen={isRightPanelOpen}
        />
        {selectedProject ? (
          <ProjectView
            isLoadingAgents={isLoadingAgents}
            onNewConversationStart={onNewConversationStart}
          />
        ) : isChat ? (
          <ChatRoom
            isLoadingAgents={isLoadingAgents}
            onNewConversationStart={onNewConversationStart}
          />
        ) : (
          <div className="flex theme-surface-muted h-full">
            <ManageAgents />
          </div>
        )}
      </div>
      {selectedMessage && selectedMessage?.type === 'compare' && (
        <ComparisonWindow isHistoryOpened={showHistory} />
      )}
      {selectedMessage && selectedMessage?.type === 'citations' && <Citations />}
      {selectedMessage && selectedMessage?.type === 'maximize' && (
        <CodeWindow isHistoryOpened={showHistory} />
      )}
      {/* not attested && ismodalopen(fullscreen) or openSidebar (workspacedrawer) */}
      {!hasAttested && (isModalOpen || openSidebar) && (
        <Flyer
          isOpen={isModalOpen || openSidebar}
          toggle={() => setIsModalOpen(false)}
          onClose={() => {
            return isDrawerMode ? (setIsModalOpen(false), closeAliDrawer()) : setIsModalOpen(false)
          }}
          header="Centrali AI"
          src={AliVideo}
          backdrop="static"
          isAttestationWindow={true}
          setHasAttested={setHasAttested}
        />
      )}
      {/* need below code for image chat feature */}
      {attachments?.previewFile && (
        <ImagePreviewModal onClose={() => closeImagePreview()} image={attachments.previewFile} />
      )}
    </div>
  )
}

export default ChatWindow
